/* eslint-disable */
import React, { useEffect, useState } from "react";

function ComparisonModal({
  isOpen,
  onClose,
  item,
  sfIds = { locations: {}, markets: {} }
}) {
  if (!isOpen || !item) return null;

  const { company, members = [], opportunities = [] } = item;
  const allInvoices = item.invoices || [];

  /* -------------------- Effects -------------------- */
  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  /* -------------------- State -------------------- */
  const [openOppIdx, setOpenOppIdx] = useState(null);
  const [openInvoiceIdx, setOpenInvoiceIdx] = useState(null);

  /* -------------------- Helpers -------------------- */
  const formatDate = (val) => {
    if (!val) return "-";
    const d = new Date(val);
    return isNaN(d) ? "-" : d.toLocaleDateString();
  };

  const formatDOB = (val) => {
    if (!val) return "-";
    const d = new Date(val);
    return isNaN(d)
      ? "-"
      : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC"
      });
  };

  const formatAddress = (address) => {
    if (!address) return "-";
    let addr = address;

    if (typeof address === "string") {
      try {
        addr = JSON.parse(address);
      } catch {
        return address;
      }
    }

    const {
      line1,
      line2,
      street,
      city,
      state,
      postalCode,
      zip,
      country
    } = addr || {};

    return [
      line1 || street,
      line2,
      city,
      state,
      postalCode || zip,
      country
    ]
      .filter(Boolean)
      .join(", ");
  };

  const renderValue = (val, key = "") => {
    if (val === null || val === undefined || val === "") return "-";
    const lower = key.toLowerCase();
    if (lower.includes("birth") || lower === "dob") return formatDOB(val);
    if (lower.includes("address")) return formatAddress(val);
    if (typeof val === "object") return JSON.stringify(val);
    return val;
  };

  const isMissing = (v) => v === null || v === undefined || v === "";

  const requiredFieldsByType = {
    Coworking: [
      "stageName",
      "contactId",
      "accountId",
      "ownerId",
      "tourDetails",
      "membershipType",
      "amount",
      "typeOfRevenue",
      "offer",
      "startDate",
      "endDate",
      "source",
      "campaign",
      "market",
      "location",
      "reason",
      "dedicatedDeskNumber",
      "notes"
    ],
    "Day Pass": [
      "Pipeline Stage",
      "Contact Associated",
      "Account Associated",
      "Opportunity Owner",
      "Tour Details",
      "Day Pass Type",
      "Revenue Generated",
      "Offer",
      "Quantity",
      "Source",
      "Campaign",
      "Market",
      "Location",
      "Reason",
      "Notes"
    ],
    "Meeting & Events": [
      "stageName",
      "contactAssociated",
      "accountAssociated",
      "ownerId",
      "tourDetails",
      "meetingStart",
      "amount",
      "offer",
      "typeOfBooking",
      "reasonForBooking",
      "meetingStart",
      "meetingEnd",
      "meetingRoom",
      "eventSpace",
      "numberOfAttendees",
      "source",
      "campaign",
      "market",
      "location",
      "reason",
      "notes"
    ],
    "Free Life Events": [
      "stageName",
      "contactId",
      "accountId",
      "ownerId",
      "tourDetails",
      "meetingStart",
      "amount",
      "offer",
      "typeOfBooking",
      "reasonForBooking",
      "meetingStart",
      "meetingEnd",
      "meetingRoom",
      "eventSpace",
      "numberOfAttendees",
      "source",
      "campaign",
      "market",
      "location",
      "reason",
      "notes"
    ],
    "Scholars": [
      "stageName",
      "contactAssociated",
      "accountAssociated",
      "ownerId",
      "tourDetails",
      "typeOfScholar",
      "amount",
      "offer",
      "cause",
      "ownershipStatus",
      "startDate",
      "endDate",
      "market",
      "location",
      "campaign",
      "source",
      "reason",
      "notes"
    ],
    "Private Office": [
      "stageName",
      "contactId",
      "accountId",
      "ownerId",
      "tourDetails",
      "membershipType",
      "amount",
      "typeOfRevenue",
      "offer",
      "startDate",
      "endDate",
      "closeDate",
      "source",
      "campaign",
      "market",
      "location",
      "officeNumber",
      "reason",
      "notes"
    ]
  };

  const isRequiredMissing = (opp, field) => {
    if (!opp || !field) return false;

    const type = opp.type; // The Opportunity type (Coworking, Day Pass, etc.)
    const requiredFields = requiredFieldsByType[type] || [];
    return requiredFields.includes(field) && isMissing(opp[field]);
  };

  /* -------------------- LEVEL COLOR LOGIC -------------------- */
  const getRowColor = (field, sfVal, orndVal, opp = {}) => {
    const key = field.toLowerCase();

    const missing = isMissing(sfVal) || isMissing(orndVal);
    const mismatch = sfVal !== orndVal;

    // Normalize addresses if the field is "address"
    if (key.includes("address")) {
      const normalizedSfAddress = normalizeAddress(sfVal);
      const normalizedOrndAddress = normalizeAddress(orndVal);

      // If the addresses match after normalization, avoid the purple mismatch color
      if (normalizedSfAddress === normalizedOrndAddress) {
        return "bg-white"; // No mismatch, no color
      }

      // If the address does not match, apply the purple color
      return "bg-purple-100";
    }

    // RED for required missing fields in Salesforce Opportunity
    if (isRequiredMissing(opp, field)) {
      return "bg-red-100";
    }

    // Level 2: yellow for mismatches
    if (
      (["booking", "membership", "opportunity"].some(k => key.includes(k)) && mismatch) ||
      (key.includes("email") && (missing || mismatch))
    ) {
      return "bg-yellow-100";
    }

    // Level 3: purple for dates and addresses mismatches
    if (
      ["dob", "date of birth", "dol", "date of leaving", "address"].some(k =>
        key.includes(k)
      ) && (missing || mismatch)
    ) {
      return "bg-purple-100";
    }

    return "bg-white";
  };

  // Helper to normalize address for comparison
  const normalizeAddress = (address) => {
    if (!address) return "";

    // If address is a string, try to parse it as JSON first (it could be an object in string form)
    let addrObj = address;
    if (typeof address === "string") {
      try {
        addrObj = JSON.parse(address);
      } catch (e) {
        // If parsing fails, assume the address is a simple string
        return address.trim().toLowerCase();
      }
    }

    // Normalize address fields (you may customize the fields used for matching)
    const {
      line1,
      line2,
      street,
      city,
      state,
      postalCode,
      zip,
      country
    } = addrObj || {};

    // Normalize each part of the address (trim and lowercase)
    return [
      line1 || street,
      line2,
      city,
      state,
      postalCode || zip,
      country
    ]
      .filter(Boolean)
      .map(part => part.trim().toLowerCase()) // Normalize each part
      .join(", ");
  };

  /* -------------------- Comparison Table -------------------- */
  const renderComparisonTable = (fields = {}, sf = {}, ornd = {}) => {
    const normalized = { ...fields };
    delete normalized.address;

    normalized.Address = {
      salesforce: sf.billingAddress || sf.address,
      officernd: ornd.address
    };

    return (
      <table className="w-full table-auto border border-gray-200 mb-6">
        <thead className="bg-gray-100">
          <tr>
            <th className="p-2 text-left">Field</th>
            <th className="p-2 text-left">Salesforce</th>
            <th className="p-2 text-left">OfficeRnD</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(normalized).map((key, idx) => {
            const sfVal = normalized[key]?.salesforce;
            const orndVal = normalized[key]?.officernd;
            return (
              <tr
                key={key}
                className={`${getRowColor(key, sfVal, orndVal)} hover:bg-gray-100`}
              >
                <td className="p-2 font-medium">{key}</td>
                <td className="p-2">{renderValue(sfVal, key)}</td>
                <td className="p-2">{renderValue(orndVal, key)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  /* -------------------- Opportunities -------------------- */
  const badge = (text, type) => {
    const map = {
      success: "bg-green-100 text-green-700",
      error: "bg-red-100 text-red-500",
      warning: "bg-yellow-100 text-yellow-800",
      info: "bg-gray-200 text-gray-800"
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[type]}`}>
        {text}
      </span>
    );
  };

  const getOppStatus = (opp) => {
    const s = opp.stageName?.toLowerCase() || "";
    if (s.includes("won")) return badge("Won", "success");
    if (s.includes("lost")) return badge("Lost", "error");
    return badge(opp.stageName || "Open", "info");
  };

  const renderOpportunitiesTable = () => {
    if (!opportunities.length) {
      return (
        <div className="border border-dashed rounded-lg p-6 text-center text-gray-500">
          No Salesforce opportunities found.
        </div>
      );
    }

    const allowedOppFields = [
      "amount",
      "campaign",
      "cause",
      "closeDate",
      "createdAt",
      "dayPassType",
      "dealTypes",
      "dedicatedDeskNumber",
      "description",
      "endDate",
      "eventSpace",
      "forecastCategory",
      "Pipeline",
      "location",
      "lostReason",
      "market",
      "meetingEnd",
      "meetingRoom",
      "meetingStart",
      "membershipType",
      "name",
      "notes",
      "numberOfAttendees",
      "offer",
      "ownerId",
      "ownershipStatus",
      "primaryCampaignSource",
      "reason",
      "reasonForBooking",
      "resource",
      "resourceType",
      "source",
      "stageName",
      "Touring",
      "startDate",
      "termEnd",
      "termLength",
      "termStart",
      "tourDetails",
      "type",
      "Coworking",
      "typeOfBooking",
      "typeOfRevenue"
    ];

    const renderAllFieldsGrid = (obj = {}) => {
      return (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          {allowedOppFields.map((key) => {
            if (!(key in obj)) return null;
            return (
              <div
                key={key}
                className={`text-sm ${getRowColor(key, obj[key], null, obj) // pass obj to check required
                  }`}
              >
                <div className="font-medium text-gray-600">{key}</div>
                <div className="text-gray-800 break-words">{renderValue(obj[key], key)}</div>
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <table className="w-full table-auto border border-gray-200 mb-6">
        <thead className="bg-gray-100 sticky top-0 z-10">
          <tr>
            <th className="p-2 text-left">Opportunity</th>
            <th className="p-2 text-left">Stage</th>
            <th className="p-2 text-left">Amount</th>
            <th className="p-2 text-left">Close Date</th>
            <th className="p-2 text-left">Location</th>
          </tr>
        </thead>
        <tbody>
          {opportunities.map((o, idx) => {
            const opp = o.salesforce || o;
            return (
              <React.Fragment key={opp.id || idx}>
                <tr
                  className={`${idx % 2 ? "bg-gray-50" : "bg-white"} cursor-pointer hover:bg-gray-100`}
                  onClick={() =>
                    setOpenOppIdx(openOppIdx === idx ? null : idx)
                  }
                >
                  <td className="p-2 font-medium">{opp.name}</td>
                  <td className="p-2">{getOppStatus(opp)}</td>
                  <td className="p-2">${opp.amount ?? "-"}</td>
                  <td className="p-2">{formatDate(opp.closeDate)}</td>
                  <td className="p-2">
                    {sfIds.locations?.[opp.location] || opp.location || "-"}
                  </td>
                </tr>

                {openOppIdx === idx && (
                  <tr className="bg-gray-50">
                    <td colSpan={5} className="p-4">
                      <h4 className="font-semibold mb-3 text-gray-700">
                        Opportunity Details
                      </h4>

                      {renderAllFieldsGrid(opp)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };



  /* -------------------- Invoices -------------------- */
  const getInvoiceStatusBadge = (inv) => {
    if (inv.isPaid) return badge("Paid", "success");
    if (inv.isFailed) return badge("Failed", "error");
    return badge(inv.status || "Unpaid", "warning");
  };

  const renderInvoicesTable = () => {
    if (!allInvoices.length) {
      return (
        <div className="border border-dashed rounded-lg p-6 text-center text-gray-500">
          No OfficeRnD invoices found.
        </div>
      );
    }

    return (
      <table className="w-full table-auto border border-gray-200 mb-6">
        <thead className="bg-gray-100">
          <tr>
            <th className="p-2 text-left">Invoice #</th>
            <th className="p-2 text-left">Status</th>
            <th className="p-2 text-left">Invoice Date</th>
            <th className="p-2 text-left">Total</th>
            <th className="p-2 text-left">Location</th>
          </tr>
        </thead>
        <tbody>
          {allInvoices.map((inv, idx) => (
            <React.Fragment key={inv._id || idx}>
              <tr
                className="cursor-pointer hover:bg-gray-100"
                onClick={() =>
                  setOpenInvoiceIdx(openInvoiceIdx === idx ? null : idx)
                }
              >
                <td className="p-2 font-medium">{inv.number || "-"}</td>
                <td className="p-2">{getInvoiceStatusBadge(inv)}</td>
                <td className="p-2">{formatDate(inv.date)}</td>
                <td className="p-2 font-semibold">
                  ${inv.baseTotal?.toFixed(2) ?? "-"}
                </td>
                <td className="p-2">
                  {sfIds.locations?.[inv.location] || inv.location || "-"}
                </td>
              </tr>

              {/* Invoice Lines */}
              {openInvoiceIdx === idx && inv.lines?.length > 0 && (
                <tr className="bg-gray-50">
                  <td colSpan={5} className="p-4">
                    <table className="w-full text-sm border border-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="p-2 text-left">Description</th>
                          <th className="p-2 text-left">Qty</th>
                          <th className="p-2 text-left">Unit Price</th>
                          <th className="p-2 text-left">Line Total</th>
                          <th className="p-2 text-left">Period</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inv.lines.map((l, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2">{l.description || "-"}</td>
                            <td className="p-2">{l.quantity ?? "-"}</td>
                            <td className="p-2">
                              ${(l.price ?? l.unitPrice ?? 0).toFixed(2)}
                            </td>
                            <td className="p-2">
                              ${l.baseTotal?.toFixed(2) ?? "-"}
                            </td>
                            <td className="p-2">
                              {formatDate(l.startDate)} – {formatDate(l.endDate)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    );
  };


  /* -------------------- Render -------------------- */
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b p-5 flex justify-between items-center">
          <h2 className="text-2xl font-bold">Company Comparison</h2>
          <button
            className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200"
            onClick={onClose}
          >
            ✕ Close
          </button>
        </div>

        <div className="p-6">
          <h3 className="text-lg font-semibold mb-3">Company Details</h3>
          {renderComparisonTable(company.fields, company.salesforce, company.officernd)}

          <h3 className="text-lg font-semibold mb-3">
            Members ({members.length})
          </h3>
          {members.length === 0
            ? <p className="text-gray-500 mb-6">No members found.</p>
            : members.map((m, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg p-4 mb-4"
              >
                <div className="font-semibold mb-2">
                  {m.salesforce?.fullName || m.officernd?.name || "Member"}
                </div>
                {renderComparisonTable(m.fields, m.salesforce, m.officernd)}
              </div>
            ))}

          <h3 className="text-lg font-semibold mb-3">
            Salesforce Opportunities ({opportunities.length})
          </h3>
          {renderOpportunitiesTable()}

          <h3 className="text-lg font-semibold mb-3">
            OfficeRnD Invoices ({allInvoices.length})
          </h3>
          {renderInvoicesTable()}

        </div>
      </div>
    </div>
  );
}

export default ComparisonModal;
